import { Button, Checkbox, Input, Spinner, Text, makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { login } from "../api/client";
import { signInViaBrowser, SsoCancelledError, SsoTimeoutError } from "../auth/browserSso";
import { brand } from "../config/brand";
import { openExternal } from "../variant-c/services/openExternal";

const REMEMBER_KEY = "asthra_remember_email";
const TNC_ACCEPTED_KEY = "asthra_tnc_accepted";

// Support link target — a verified-live page on the marketing site, and
// listed in the AppSource manifest's <AppDomains>.
//
// This link is NOT feature-flagged, unlike the in-app incomplete actions.
// FeatureFlagsProvider mounts only after login, so there is no
// authenticated user to evaluate a flag against on this screen — a dead
// link here has to be fixed outright, not gated.
//
// There is deliberately no sign-up affordance: the add-in has no
// self-serve registration, so an entry point to one would be a dead end.
// Accounts are provisioned out-of-band, and Partner Center carries the
// reviewer's test credentials.
const SUPPORT_URL = "https://asthra-writer.ai/support";

// Trust Center target — same verified-live marketing host as SUPPORT_URL
// (already listed in the AppSource manifest's <AppDomains>). Surfaced on the
// auth screen (footer + beside the Terms link) so users can review our
// security/privacy/compliance posture before agreeing and signing in.
const TRUST_URL = "https://asthra-writer.ai/trust";

// Step-by-step product flow shown on the login screen — a vertical reflow of
// the marketing deck's Input → Draft → Gaps → Iterate diagram, kept terse for
// the taskpane width.
const STEPS: ReadonlyArray<{
  badge: string;
  kicker: string;
  title: string;
  isInput?: boolean;
}> = [
  { badge: "IN", kicker: "Input", title: "Load your source library", isInput: true },
  { badge: "01", kicker: "Step 01", title: "Get a fully cited first draft" },
  { badge: "02", kicker: "Step 02", title: "See compliance gaps flagged in-line" },
  { badge: "03", kicker: "Step 03", title: "Clear flags, sections re-scored" },
];

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 28px 24px",
    gap: "0px",
    height: "100%",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: "var(--background)",
  },
  logoArea: {
    marginTop: "0px",
    marginBottom: "0px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
  },
  blurb: {
    color: "var(--text-primary)",
    textAlign: "center" as const,
    fontSize: "0.8125rem",
    fontWeight: 600,
    lineHeight: 1.4,
    maxWidth: "300px",
    marginBottom: "14px",
  },
  // Vertical stepper — a taskpane-friendly reflow of the deck's horizontal
  // Input → Draft → Gaps → Iterate flow. The steps share a single connecting
  // rail drawn behind the badges via a ::before on the list container.
  stepper: {
    position: "relative" as const,
    width: "100%",
    maxWidth: "300px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginBottom: "18px",
    paddingLeft: "4px",
    "::before": {
      content: '""',
      position: "absolute" as const,
      left: "15px",
      top: "12px",
      bottom: "12px",
      width: "2px",
      backgroundColor: brand.colorPrimaryLight,
    },
  },
  step: {
    position: "relative" as const,
    display: "flex",
    alignItems: "center",
    gap: "11px",
    zIndex: 1,
  },
  stepBadge: {
    flexShrink: 0,
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.5625rem",
    fontWeight: 700,
    letterSpacing: "0.2px",
    color: "var(--background)",
    backgroundColor: brand.colorPrimary,
  },
  stepBadgeInput: {
    backgroundColor: brand.colorPrimaryMid,
  },
  stepBody: {
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  stepKicker: {
    fontSize: "0.5rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
    color: brand.colorPrimaryMid,
  },
  stepTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--text-primary)",
    lineHeight: 1.25,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    width: "100%",
    maxWidth: "320px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "0.375rem",
  },
  error: {
    color: "var(--accent-red)",
    fontSize: "0.75rem",
    textAlign: "center" as const,
  },
  loginButton: {
    marginTop: "4px",
    height: "44px",
    borderRadius: "22px",
    fontSize: "0.875rem",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  // "or" divider between the password form and the SSO buttons.
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "320px",
    margin: "14px 0 10px",
    color: "var(--text-secondary)",
    fontSize: "0.6875rem",
    textTransform: "uppercase" as const,
    letterSpacing: "0.5px",
    "::before": {
      content: '""',
      flex: 1,
      height: "1px",
      backgroundColor: brand.colorPrimaryLight,
    },
    "::after": {
      content: '""',
      flex: 1,
      height: "1px",
      backgroundColor: brand.colorPrimaryLight,
    },
  },
  ssoGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    width: "100%",
    maxWidth: "320px",
  },
  ssoButton: {
    height: "44px",
    borderRadius: "22px",
    fontSize: "0.875rem",
    fontWeight: 600,
    justifyContent: "center",
  },
  footerLinks: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.75rem",
    marginTop: "14px",
  },
  // Rendered as a <button> rather than a <span> so the links are
  // keyboard-focusable and announced as actionable — AppSource review
  // includes a keyboard-only navigation pass. The border/background/padding
  // resets keep it looking like inline text despite being a button.
  footerLink: {
    fontSize: "0.75rem",
    color: brand.colorPrimary,
    cursor: "pointer",
    textDecoration: "none",
    backgroundColor: "transparent",
    border: "none",
    padding: "0px",
    margin: "0px",
    // fontFamily/lineHeight only — NOT the `font` shorthand, which would
    // expand to include font-size and clobber the 0.75rem above.
    fontFamily: "inherit",
    lineHeight: "inherit",
    ":hover": {
      textDecoration: "underline",
    },
  },
});

interface Props {
  onLoginSuccess: (username: string) => void;
}

export default function LoginForm({ onLoginSuccess }: Props) {
  const styles = useStyles();
  const savedEmail = localStorage.getItem(REMEMBER_KEY) || "";
  const hasPreviouslyAccepted = localStorage.getItem(TNC_ACCEPTED_KEY) === "true";
  const [username, setUsername] = useState(savedEmail);
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(!!savedEmail);
  const [termsAccepted, setTermsAccepted] = useState(hasPreviouslyAccepted);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rateLimitSecondsLeft, setRateLimitSecondsLeft] = useState<number | null>(null);
  // True while the browser sign-in is open and we're polling for its result.
  // Kept separate from the password `loading` so the two paths don't clobber
  // each other's UI.
  const [browserBusy, setBrowserBusy] = useState(false);
  const browserAbort = useRef<AbortController | null>(null);

  const busy = loading || browserBusy;
  const rateLimited = rateLimitSecondsLeft !== null && rateLimitSecondsLeft > 0;

  useEffect(() => {
    if (rateLimitSecondsLeft === null || rateLimitSecondsLeft <= 0) return;
    const id = setTimeout(() => setRateLimitSecondsLeft((prev) => (prev !== null ? prev - 1 : null)), 1000);
    return () => clearTimeout(id);
  }, [rateLimitSecondsLeft]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;

    setLoading(true);
    setError(null);
    setRateLimitSecondsLeft(null);

    try {
      const resp = await login(username, password);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, username);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      if (termsAccepted) {
        localStorage.setItem(TNC_ACCEPTED_KEY, "true");
      }
      onLoginSuccess(resp.username);
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      const msg = err instanceof Error ? err.message : "Login failed";
      if (status === 429) {
        setRateLimitSecondsLeft(60);
      } else if (msg.includes("401") || status === 401) {
        setError("Invalid username or password");
      } else if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        setError("Cannot reach server. Is the backend running?");
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBrowserSignIn = async () => {
    if (!termsAccepted || busy || rateLimited) return;

    setError(null);
    setRateLimitSecondsLeft(null);
    const controller = new AbortController();
    browserAbort.current = controller;
    setBrowserBusy(true);

    try {
      const { username } = await signInViaBrowser(controller.signal);

      // Remember the identity the writer just used, exactly as the password path
      // remembers the typed email.
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, username);
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      localStorage.setItem(TNC_ACCEPTED_KEY, "true");
      onLoginSuccess(username);
    } catch (err: unknown) {
      // Cancelling is a choice, not an error — no red banner.
      if (err instanceof SsoCancelledError) return;
      if (err instanceof SsoTimeoutError) {
        setError(err.message);
        return;
      }
      const msg = err instanceof Error ? err.message : "Sign-in failed";
      if (msg.includes("Failed to fetch") || msg.includes("NetworkError")) {
        setError("Cannot reach server. Is the backend running?");
      } else {
        setError(msg);
      }
    } finally {
      setBrowserBusy(false);
      browserAbort.current = null;
    }
  };

  const cancelBrowserSignIn = () => browserAbort.current?.abort();

  return (
    <div className={styles.container}>
      <div className={styles.logoArea}>
        <img src={brand.logoPrimary} alt={brand.name} style={{ width: "140px", height: "auto" }} />
      </div>

      <Text className={styles.blurb}>Your sources in. A submission-ready draft out.</Text>

      <div className={styles.stepper} role="list" aria-label={`How ${brand.name} works`}>
        {STEPS.map((s) => (
          <div className={styles.step} role="listitem" key={s.kicker}>
            <div className={mergeClasses(styles.stepBadge, s.isInput && styles.stepBadgeInput)}>{s.badge}</div>
            <div className={styles.stepBody}>
              <span className={styles.stepKicker}>{s.kicker}</span>
              <span className={styles.stepTitle}>{s.title}</span>
            </div>
          </div>
        ))}
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <Input
            id="email"
            value={username}
            onChange={(_, data) => setUsername(data.value)}
            placeholder="Registered email ID"
            aria-label="Email"
            disabled={loading}
            size="large"
            style={{ borderRadius: "6px" }}
          />
        </div>

        <div className={styles.field}>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(_, data) => setPassword(data.value)}
            placeholder="Password"
            aria-label="Password"
            disabled={loading}
            size="large"
            style={{ borderRadius: "6px" }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <Checkbox
            checked={rememberMe}
            onChange={(_, data) => setRememberMe(!!data.checked)}
            label={<Text style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>Keep me logged in</Text>}
            size="medium"
          />

          {!hasPreviouslyAccepted && (
            <Checkbox
              checked={termsAccepted}
              onChange={(_, data) => setTermsAccepted(!!data.checked)}
              label={
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  I Agree to the{" "}
                  <a
                    href="https://asthra-writer.ai/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: brand.colorPrimary,
                      textDecoration: "underline",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Asthra AI Terms and Conditions
                  </a>
                  {" · "}
                  <a
                    href={TRUST_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: brand.colorPrimary,
                      textDecoration: "underline",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Trust Center
                  </a>
                </span>
              }
              size="medium"
            />
          )}
        </div>

        {rateLimitSecondsLeft !== null && (
          <Text className={styles.error}>
            {rateLimitSecondsLeft > 0
              ? `Too many login attempts. Please try again in ${rateLimitSecondsLeft} seconds.`
              : "You can try logging in again."}
          </Text>
        )}
        {error && rateLimitSecondsLeft === null && <Text className={styles.error}>{error}</Text>}

        <Button
          className={styles.loginButton}
          type="submit"
          disabled={busy || !username || !password || !termsAccepted || rateLimited}
          style={{
            backgroundColor: loading ? "#CCCCCC" : brand.colorPrimary,
            color: "var(--background)",
            opacity: !username || !password || !termsAccepted || rateLimited ? 0.6 : 1,
          }}
        >
          {loading ? <Spinner size="tiny" /> : "Login"}
        </Button>
      </form>

      <div className={styles.divider} aria-hidden="true">
        or
      </div>
      <div className={styles.ssoGroup}>
        {browserBusy ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Spinner size="tiny" />
              <Text style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
                Waiting for browser sign-in…
              </Text>
            </div>
            <Button className={styles.ssoButton} appearance="subtle" type="button" onClick={cancelBrowserSignIn}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            className={styles.ssoButton}
            appearance="outline"
            type="button"
            disabled={busy || !termsAccepted || rateLimited}
            onClick={handleBrowserSignIn}
          >
            Sign in with Microsoft or Google
          </Button>
        )}
      </div>
      {browserBusy && (
        <Text style={{ fontSize: "0.6875rem", color: "var(--text-secondary)", marginTop: "6px", textAlign: "center" }}>
          Complete sign-in in the browser window that just opened, then return here.
        </Text>
      )}

      <div className={styles.footerLinks}>
        <Text>
          <button
            type="button"
            className={styles.footerLink}
            style={{ fontSize: "0.6875rem" }}
            onClick={() => openExternal(SUPPORT_URL)}
          >
            Need help? Contact support
          </button>
        </Text>
        <Text>
          <button
            type="button"
            className={styles.footerLink}
            style={{ fontSize: "0.6875rem" }}
            onClick={() => openExternal(TRUST_URL)}
          >
            Trust Center
          </button>
        </Text>
      </div>
    </div>
  );
}
